import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AnnoncenewService } from '../service/annoncenew.service';
import { AwscontrolService, UploadResponse, ImageUploadResponse } from '../../services/aws/awscontrol.service';

type ImageUploadMode = 'direct' | 'imagesousproduit';
type MainImageUploadMode = 'direct' | 'imageprincipaleannonce';

@Component({
  selector: 'app-addnewannonce',
  templateUrl: './addnewannonce.component.html',
  styleUrls: ['./addnewannonce.component.scss']
})
export class AddnewannonceComponent implements OnInit {
  annonceForm: FormGroup;
  isLoading = false;
  isSubmitting = false;
  additionalImages: string[] = [];

  // ============== MAIN IMAGE STATE ==============
  mainImageUploadMode: MainImageUploadMode = 'direct';
  mainPreviewOriginalUrl: string | null = null;
  mainPreviewProcessedUrl: string | null = null;
  isAwaitingMainValidation: boolean = false;

  // ============== GALLERY STATE ==============
  galleryUploadMode: ImageUploadMode = 'direct';
  galleryPreviewOriginalUrls: string[] = [];
  galleryPreviewProcessedUrls: string[] = [];
  isAwaitingGalleryValidation: boolean = false;

  villes = [
    'Tunis', 'Ariana', 'Ben Arous', 'Manouba', 'Nabeul', 'Zaghouan',
    'Bizerte', 'Béja', 'Jendouba', 'Le Kef', 'Siliana', 'Kairouan',
    'Kasserine', 'Sidi Bouzid', 'Sousse', 'Monastir', 'Mahdia',
    'Sfax', 'Gabès', 'Médenine', 'Tataouine', 'Gafsa', 'Tozeur', 'Kebili'
  ];

  statusOptions = [
    { value: 'Vente', label: 'Vente' },
    { value: 'Location', label: 'Location' },
    { value: 'Location Vacances', label: 'Location Vacances' }
  ];

  annonceTypes = [
    { value: 'Appartements', label: 'Appartements' },
    { value: 'Bureaux', label: 'Bureaux' },
    { value: 'Commerces', label: 'Commerces' },
    { value: 'Duplexes', label: 'Duplexes' },
    { value: 'Industriel', label: 'Industriel' },
    { value: 'Lots', label: 'Lots' },
    { value: 'Studios', label: 'Studios' },
    { value: 'Terrain agricole', label: 'Terrain agricole' },
    { value: 'Villas', label: 'Villas / maisons' }
  ];

  constructor(
    private fb: FormBuilder,
    private annonceService: AnnoncenewService,
    private awsService: AwscontrolService,
    private router: Router,
    private snackBar: MatSnackBar
  ) {
    this.annonceForm = this.fb.group({
      annonceName: [''],
      adresse: [''],
      ville: [''],
      status: [''],
      typeAnnonce: [''],
      description: [''],
      prix: [''],
      phone1: [''],
      phone2: [''],
      email: [''],
      urlYoutube: [''],
      photoPrincipale: [''],
      imagesList: ['']
    });
  }

  ngOnInit(): void {}

  // ========== DESCRIPTION TOOLBAR ==========
  private insertTag(openTag: string, closeTag: string): void {
    const textarea = document.getElementById('descriptionTextarea') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const before = textarea.value.substring(0, start);
    const after = textarea.value.substring(end);

    let newText = '';
    if (selectedText.length > 0) {
      newText = before + openTag + selectedText + closeTag + after;
    } else {
      newText = before + openTag + closeTag + after;
    }
    textarea.value = newText;
    this.annonceForm.patchValue({ description: newText });

    if (selectedText.length === 0) {
      const cursorPos = before.length + openTag.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    } else {
      textarea.setSelectionRange(before.length + openTag.length, before.length + openTag.length + selectedText.length);
    }
    textarea.focus();
  }

  formatBold(): void { this.insertTag('<b>', '</b>'); }
  formatItalic(): void { this.insertTag('<i>', '</i>'); }
  formatUnderline(): void { this.insertTag('<u>', '</u>'); }

  // ========== UTILITAIRE ==========
  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      this.snackBar.open('Format non supporté (JPEG, PNG, JPG, GIF, WEBP)', 'Fermer', { duration: 3000 });
      return false;
    }
    if (file.size > maxSize) {
      this.snackBar.open('Fichier trop volumineux (max 5MB)', 'Fermer', { duration: 3000 });
      return false;
    }
    return true;
  }

  // ============================================================
  // ==================== MAIN IMAGE ============================
  // ============================================================

  setMainUploadMode(mode: MainImageUploadMode): void {
    if (this.isLoading) return;
    if (this.isAwaitingMainValidation) {
      this.cancelMainImagePreview(true);
    }
    this.mainImageUploadMode = mode;
    console.log('🔄 Mode image principale :', mode);
  }

  onMainImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    if (!this.validateImageFile(file)) {
      input.value = '';
      return;
    }

    // Aperçu local de l'original
    const reader = new FileReader();
    reader.onload = (e) => {
      this.mainPreviewOriginalUrl = (e.target?.result as string) || null;
    };
    reader.readAsDataURL(file);

    if (this.mainImageUploadMode === 'direct') {
      this.uploadMainImageDirect(file, input);
    } else {
      this.uploadMainImageViaImagePrincipaleAnnonce(file, input);
    }
  }

  private uploadMainImageDirect(file: File, input: HTMLInputElement): void {
    this.isLoading = true;
    this.awsService.uploadFile(file, 'annonces').subscribe({
      next: (response: UploadResponse) => {
        this.isLoading = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.annonceForm.patchValue({ photoPrincipale: response.urls[0] });
          this.mainPreviewOriginalUrl = null;
          this.snackBar.open('Image principale téléchargée', 'Fermer', { duration: 2000 });
          input.value = '';
        } else {
          this.snackBar.open('Erreur upload', 'Fermer', { duration: 3000 });
        }
      },
      error: (err) => {
        console.error(err);
        this.snackBar.open('Erreur upload', 'Fermer', { duration: 3000 });
        this.isLoading = false;
        this.mainPreviewOriginalUrl = null;
      }
    });
  }

  private uploadMainImageViaImagePrincipaleAnnonce(file: File, input: HTMLInputElement): void {
    this.isLoading = true;
    this.isAwaitingMainValidation = false;
    this.mainPreviewProcessedUrl = null;

    this.awsService.imageprincipaleannonce([file], 'annonces').subscribe({
      next: (response: ImageUploadResponse) => {
        this.isLoading = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.mainPreviewProcessedUrl = response.urls[0];
          this.isAwaitingMainValidation = true;
          input.value = '';
        } else {
          this.snackBar.open('Erreur traitement image', 'Fermer', { duration: 3000 });
          this.mainPreviewOriginalUrl = null;
        }
      },
      error: () => {
        this.isLoading = false;
        this.snackBar.open('Erreur traitement image', 'Fermer', { duration: 3000 });
        this.mainPreviewOriginalUrl = null;
      }
    });
  }

  validateMainImagePreview(): void {
    if (!this.mainPreviewProcessedUrl) return;

    this.annonceForm.patchValue({ photoPrincipale: this.mainPreviewProcessedUrl });

    this.mainPreviewOriginalUrl = null;
    this.mainPreviewProcessedUrl = null;
    this.isAwaitingMainValidation = false;

    this.snackBar.open('Image principale validée', 'Fermer', { duration: 2000 });
  }

  cancelMainImagePreview(silent: boolean = false): void {
    const urlToDelete = this.mainPreviewProcessedUrl;

    const doCancel = () => {
      if (urlToDelete) {
        this.awsService.deleteFile(urlToDelete).subscribe({
          next: () => console.log('🗑️ Image principale refusée supprimée du S3'),
          error: (err) => console.warn('⚠️ Impossible de supprimer du S3 :', err)
        });
      }
      this.mainPreviewOriginalUrl = null;
      this.mainPreviewProcessedUrl = null;
      this.isAwaitingMainValidation = false;
    };

    if (silent) { doCancel(); return; }

    if (confirm('Refuser cette image ? L\'image traitée sera supprimée.')) {
      doCancel();
    }
  }

  removeMainImage(): void {
    if (this.isAwaitingMainValidation) {
      this.cancelMainImagePreview();
      return;
    }
    this.annonceForm.patchValue({ photoPrincipale: '' });
  }

  previewMainImage(): string {
    return this.annonceForm.get('photoPrincipale')?.value || 'assets/images/no-image.jpg';
  }

  // ============================================================
  // ==================== GALERIE ===============================
  // ============================================================

  setGalleryUploadMode(mode: ImageUploadMode): void {
    if (this.isLoading) return;
    if (this.isAwaitingGalleryValidation) {
      this.cancelGalleryPreview(true);
    }
    this.galleryUploadMode = mode;
    console.log('🔄 Mode galerie :', mode);
  }

  onAdditionalImagesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const validFiles: File[] = [];
    for (let i = 0; i < input.files.length; i++) {
      if (this.validateImageFile(input.files[i])) {
        validFiles.push(input.files[i]);
      }
    }

    if (validFiles.length === 0) {
      input.value = '';
      return;
    }

    // Aperçu local des originaux
    this.galleryPreviewOriginalUrls = [];
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const url = (e.target?.result as string) || '';
        if (url) this.galleryPreviewOriginalUrls.push(url);
      };
      reader.readAsDataURL(file);
    });

    if (this.galleryUploadMode === 'direct') {
      this.uploadGalleryDirect(validFiles, input);
    } else {
      this.uploadGalleryViaImagesousproduit(validFiles, input);
    }
  }

  private uploadGalleryDirect(files: File[], input: HTMLInputElement): void {
    this.isLoading = true;
    this.awsService.uploadFiles(files, 'annonces').subscribe({
      next: (response: UploadResponse) => {
        this.isLoading = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.additionalImages.push(...response.urls);
          this.updateImagesList();
          this.galleryPreviewOriginalUrls = [];
          this.snackBar.open(`${response.urls.length} image(s) téléchargée(s)`, 'Fermer', { duration: 2000 });
          input.value = '';
        } else {
          this.snackBar.open('Erreur upload', 'Fermer', { duration: 3000 });
          this.galleryPreviewOriginalUrls = [];
        }
      },
      error: (err) => {
        console.error(err);
        this.snackBar.open('Erreur upload', 'Fermer', { duration: 3000 });
        this.isLoading = false;
        this.galleryPreviewOriginalUrls = [];
      }
    });
  }

  private uploadGalleryViaImagesousproduit(files: File[], input: HTMLInputElement): void {
    this.isLoading = true;
    this.isAwaitingGalleryValidation = false;
    this.galleryPreviewProcessedUrls = [];

    this.awsService.imagesousproduit(files, 'annonces').subscribe({
      next: (response: ImageUploadResponse) => {
        this.isLoading = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.galleryPreviewProcessedUrls = response.urls;
          this.isAwaitingGalleryValidation = true;
          input.value = '';
        } else {
          this.snackBar.open('Erreur traitement images', 'Fermer', { duration: 3000 });
          this.galleryPreviewOriginalUrls = [];
        }
      },
      error: () => {
        this.isLoading = false;
        this.snackBar.open('Erreur traitement images', 'Fermer', { duration: 3000 });
        this.galleryPreviewOriginalUrls = [];
      }
    });
  }

  validateGalleryPreview(): void {
    if (!this.galleryPreviewProcessedUrls.length) return;

    this.additionalImages.push(...this.galleryPreviewProcessedUrls);
    this.updateImagesList();

    this.galleryPreviewOriginalUrls = [];
    this.galleryPreviewProcessedUrls = [];
    this.isAwaitingGalleryValidation = false;

    this.snackBar.open(`${this.additionalImages.length} image(s) dans la galerie`, 'Fermer', { duration: 2000 });
  }

  cancelGalleryPreview(silent: boolean = false): void {
    const urlsToDelete = [...this.galleryPreviewProcessedUrls];

    const doCancel = () => {
      urlsToDelete.forEach(url => {
        this.awsService.deleteFile(url).subscribe({
          next: () => console.log('🗑️ Image refusée supprimée du S3'),
          error: (err) => console.warn('⚠️ Impossible de supprimer du S3 :', err)
        });
      });
      this.galleryPreviewOriginalUrls = [];
      this.galleryPreviewProcessedUrls = [];
      this.isAwaitingGalleryValidation = false;
    };

    if (silent) { doCancel(); return; }

    if (confirm('Refuser ces images ? Elles seront supprimées.')) {
      doCancel();
    }
  }

  moveImageUp(index: number): void {
    if (index > 0) {
      const item = this.additionalImages[index];
      this.additionalImages.splice(index, 1);
      this.additionalImages.splice(index - 1, 0, item);
      this.updateImagesList();
    }
  }

  moveImageDown(index: number): void {
    if (index < this.additionalImages.length - 1) {
      const item = this.additionalImages[index];
      this.additionalImages.splice(index, 1);
      this.additionalImages.splice(index + 1, 0, item);
      this.updateImagesList();
    }
  }

  removeAdditionalImage(index: number): void {
    this.additionalImages.splice(index, 1);
    this.updateImagesList();
  }

  private updateImagesList(): void {
    this.annonceForm.patchValue({ imagesList: this.additionalImages.join(',') });
  }

  // ========== SOUMISSION ==========
  onSubmit(): void {
    if (this.isAwaitingMainValidation) {
      this.snackBar.open('Veuillez valider ou refuser l\'image principale', 'Fermer', { duration: 3000 });
      return;
    }
    if (this.isAwaitingGalleryValidation) {
      this.snackBar.open('Veuillez valider ou refuser les images de la galerie', 'Fermer', { duration: 3000 });
      return;
    }

    this.isSubmitting = true;

    const formData: any = {
      annonceName: this.annonceForm.value.annonceName,
      adresse: this.annonceForm.value.adresse,
      ville: this.annonceForm.value.ville,
      status: this.annonceForm.value.status,
      typeAnnonce: this.annonceForm.value.typeAnnonce,
      description: this.annonceForm.value.description,
      prix: this.annonceForm.value.prix,
      phone1: this.annonceForm.value.phone1,
      phone2: this.annonceForm.value.phone2 || undefined,
      email: this.annonceForm.value.email || undefined,
      urlYoutube: this.annonceForm.value.urlYoutube || undefined,
      photoPrincipale: this.annonceForm.value.photoPrincipale,
      imagesList: this.annonceForm.value.imagesList || undefined
    };

    this.annonceService.createAnnonce(formData).subscribe({
      next: () => {
        this.snackBar.open('Annonce créée avec succès !', 'Fermer', { duration: 3000 });
        this.router.navigate(['/annoncenew']);
      },
      error: (error) => {
        console.error(error);
        this.snackBar.open('Erreur lors de la création', 'Fermer', { duration: 3000 });
        this.isSubmitting = false;
      }
    });
  }

  onCancel(): void {
    if (this.annonceForm.dirty) {
      if (confirm('Modifications non enregistrées. Quitter ?')) {
        this.router.navigate(['/annoncenew']);
      }
    } else {
      this.router.navigate(['/annoncenew']);
    }
  }
}